import { useDocumentTitle } from "@/lib/use-document-title";
import { useState, useMemo } from "react";
import { StaffLayout } from "@/components/StaffLayout";
import { useMembers, useStores, useMemberMutations, useMemberBenefitLogs, useMemberEventRegistrations, useAttendanceCounts, useEvents } from "@/lib/data-hooks";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { MemberFilterPanel, useMemberFilter } from "@/components/MemberFilter";
import { X, Trash2, Users, Download, CheckSquare, Square, Lock, Tag, Calendar, CheckCircle2, Clock } from "lucide-react";
import { useConfirm } from "@/components/ConfirmDialog";
import { EmptyState, RowSkeleton } from "@/components/EmptyState";
import { downloadCsv } from "@/lib/csv";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth-context";

const STATUSES = ["aktiv", "inaktiv", "pausad", "uppsagd"] as const;

function CustomersPage() {
  useDocumentTitle("Kundregister — ÅF-Portal");
  const { staff } = useAuth();
  const isAdmin = staff?.role === "admin";
  const { data: members = [], isLoading } = useMembers();
  const { data: stores = [] } = useStores();
  const { data: attendance } = useAttendanceCounts();
  const { remove, setStatus } = useMemberMutations();
  const confirm = useConfirm();
  const storeMap = new Map(stores.map((s) => [s.id, s.name]));

  const { state, setters, filtered, clear } = useMemberFilter(members);
  const [selected, setSelected] = useState<string | null>(null);
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const sel = members.find((c) => c.id === selected);

  const filteredIds = useMemo(() => filtered.map((m) => m.id), [filtered]);
  const allCheckedInView =
    filteredIds.length > 0 && filteredIds.every((id) => checkedIds.has(id));
  const anyChecked = checkedIds.size > 0;

  function toggleAll() {
    if (allCheckedInView) {
      const next = new Set(checkedIds);
      filteredIds.forEach((id) => next.delete(id));
      setCheckedIds(next);
    } else {
      const next = new Set(checkedIds);
      filteredIds.forEach((id) => next.add(id));
      setCheckedIds(next);
    }
  }

  function toggleRow(id: string) {
    const next = new Set(checkedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setCheckedIds(next);
  }

  function clearSelection() {
    setCheckedIds(new Set());
  }

  function exportSelected() {
    const rows = members
      .filter((m) => checkedIds.has(m.id))
      .map((m) => ({
        medlemsnummer: m.member_number,
        fornamn: m.first_name,
        efternamn: m.last_name,
        epost: m.email,
        telefon: m.phone ?? "",
        adress: m.address ?? "",
        postnummer: m.zip ?? "",
        ort: m.city ?? "",
        operator: m.operator ?? "",
        telefon_marke: m.phone_brand ?? "",
        telefon_modell: m.phone_model ?? "",
        status: m.status,
        butik: m.store_id ? storeMap.get(m.store_id) ?? "" : "",
        medlem_sedan: m.member_since,
        gdpr_samtycke: m.gdpr_consent ? "ja" : "nej",
      }));
    downloadCsv(`medlemmar-markerade-${new Date().toISOString().slice(0, 10)}.csv`, rows);
    toast.success(`Exporterade ${rows.length} medlemmar`);
  }

  async function deleteSelected() {
    const ids = Array.from(checkedIds);
    const ok = await confirm({
      title: `Radera ${ids.length} medlemmar?`,
      description: "Alla markerade medlemmars personuppgifter anonymiseras. Fakturahistorik behålls. Kan återställas inom 30 dagar.",
      confirmText: `Radera ${ids.length}`,
      destructive: true,
    });
    if (!ok) return;
    let done = 0;
    for (const id of ids) {
      try {
        await remove.mutateAsync({ id, reason: "Bulk GDPR-radering" });
        done++;
      } catch (e) {
        // continue
      }
    }
    toast.success(`Raderade ${done} av ${ids.length}`);
    clearSelection();
  }

  async function handleDelete(id: string, name: string) {
    const ok = await confirm({
      title: `Radera ${name} permanent?`,
      description: "Medlemmen, inloggningskontot och ev. aktiv prenumeration tas bort. Fakturahistorik behålls för bokföring. Mejladressen kan registreras igen direkt. Åtgärden kan inte ångras.",
      confirmText: "Ja, radera permanent",
      destructive: true,
    });
    if (!ok) return;
    try {
      await remove.mutateAsync({ id, reason: "GDPR-radering på begäran" });
      toast.success(`${name} raderad`);
      setSelected(null);
    } catch (e: any) {
      toast.error(`Kunde inte radera: ${e?.message ?? "okänt fel"}`);
    }
  }

  return (
    <StaffLayout title="Kundregister">
      {!isAdmin && (
        <div className="mb-4 flex items-start gap-2.5 p-3 rounded-xl border border-border bg-muted/50 text-xs text-muted-foreground">
          <Lock size={14} className="mt-0.5 shrink-0" />
          <span>Du visar kundregistret som personal. Ändringar av status och radering av personuppgifter kräver admin-behörighet.</span>
        </div>
      )}
      <div className="mb-5">
        <MemberFilterPanel state={state} setters={setters} stores={stores} members={members} filteredCount={filtered.length} onClear={clear} />
      </div>



      {anyChecked && (
        <div className="mb-4 flex items-center justify-between gap-3 p-3 rounded-xl bg-[var(--color-ink)] text-white shadow-lg sticky top-0 z-20">
          <div className="flex items-center gap-3 text-sm">
            <span className="font-display font-semibold">{checkedIds.size}</span> markerade
            <button onClick={clearSelection} className="text-white/60 hover:text-white text-xs underline">Avmarkera</button>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportSelected}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-xs font-display"
            >
              <Download size={13} /> Exportera CSV
            </button>
            {isAdmin && (
              <button
                onClick={deleteSelected}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-destructive/80 hover:bg-destructive text-xs font-display"
              >
                <Trash2 size={13} /> Radera
              </button>
            )}
          </div>
        </div>
      )}


      <div className="card-soft overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted">
            <tr className="text-left">
              <th className="py-3 px-4 w-10">
                <button onClick={toggleAll} aria-label={allCheckedInView ? "Avmarkera alla" : "Markera alla"} className="text-muted-foreground hover:text-foreground">
                  {allCheckedInView ? <CheckSquare size={16} /> : <Square size={16} />}
                </button>
              </th>
              {["Namn", "Medlemsnr", "Butik", "Säljare", "Telefonnummer", "Operatör", "Status", ""].map((h) => (
                <th key={h} className="py-3 px-4 font-display font-semibold text-xs uppercase tracking-wider text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && <RowSkeleton rows={6} cols={9} />}
            {!isLoading && filtered.map((c) => {
              const checked = checkedIds.has(c.id);
              return (
                <tr key={c.id} className={`border-t border-border hover:bg-muted/50 transition ${checked ? "bg-primary/5" : ""}`}>
                  <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => toggleRow(c.id)} aria-label={checked ? `Avmarkera ${c.first_name}` : `Markera ${c.first_name}`} className="text-muted-foreground hover:text-primary">
                      {checked ? <CheckSquare size={16} className="text-primary" /> : <Square size={16} />}
                    </button>
                  </td>
                  <td className="py-3 px-4 font-display font-semibold cursor-pointer" onClick={() => setSelected(c.id)}>
                    <span className="inline-flex items-center gap-2">
                      {c.first_name} {c.last_name}
                      {!!attendance?.get(c.id) && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 text-[11px] font-display font-semibold" title="Antal kvällar på event">
                          <CheckCircle2 size={11} /> {attendance.get(c.id)}
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="py-3 px-4 font-mono text-xs cursor-pointer" onClick={() => setSelected(c.id)}>{c.member_number}</td>
                  <td className="py-3 px-4 cursor-pointer" onClick={() => setSelected(c.id)}>{c.store_id ? storeMap.get(c.store_id) : "—"}</td>
                  <td className="py-3 px-4 cursor-pointer" onClick={() => setSelected(c.id)}>{(c as any).salesperson ?? "—"}</td>
                  <td className="py-3 px-4 text-muted-foreground cursor-pointer" onClick={() => setSelected(c.id)}>{c.phone ?? "—"}</td>
                  <td className="py-3 px-4 text-muted-foreground cursor-pointer" onClick={() => setSelected(c.id)}>{c.operator ?? "—"}</td>
                  <td className="py-3 px-4 cursor-pointer" onClick={() => setSelected(c.id)}>
                    <div className="flex flex-col gap-1">
                      <span className={`inline-flex w-fit px-2.5 py-1 rounded-full text-xs font-display font-semibold ${c.status === "aktiv" ? "bg-[var(--color-success)]/15 text-[var(--color-success)]" : c.status === "uppsagd" ? "bg-[var(--color-warning,#f59e0b)]/15 text-[var(--color-warning,#f59e0b)]" : "bg-[var(--color-destructive)]/15 text-[var(--color-destructive)]"}`}>{c.status}</span>
                      {c.cancel_at && new Date(c.cancel_at) > new Date() && (
                        <span className="text-[11px] text-muted-foreground">Avslutas {new Date(c.cancel_at).toLocaleDateString("sv-SE", { day: "numeric", month: "short" })}</span>
                      )}
                      {!c.cancel_at && (c as any).binding_until && new Date((c as any).binding_until) > new Date() && (
                        <span className="text-[11px] text-primary">Bindning t.o.m. {new Date((c as any).binding_until).toLocaleDateString("sv-SE", { day: "numeric", month: "short" })}</span>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-right text-primary text-sm cursor-pointer" onClick={() => setSelected(c.id)}>Visa →</td>
                </tr>
              );
            })}
            {!isLoading && !members.length && (
              <tr><td colSpan={9} className="py-0">
                <EmptyState
                  icon={Users}
                  title="Inga kunder ännu"
                  description="När någon registrerar sig som medlem dyker de upp här."
                  ctaLabel="Hjälp kund att registrera"
                  ctaTo="/access/skapa-konto?assisted=1"
                  className="my-4"
                />
              </td></tr>
            )}
            {!isLoading && !!members.length && !filtered.length && (
              <tr><td colSpan={9} className="py-10 text-center text-muted-foreground">Inga kunder matchar filtret. <button onClick={clear} className="text-primary underline ml-1">Återställ filter</button></td></tr>
            )}
          </tbody>
        </table>
      </div>

      {sel && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setSelected(null)}>
          <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-border flex items-start justify-between sticky top-0 bg-white">
              <div>
                <h2 className="!text-2xl">{sel.first_name} {sel.last_name}</h2>
                <div className="text-sm text-muted-foreground font-mono">{sel.member_number} · {sel.store_id ? storeMap.get(sel.store_id) : "—"}</div>
              </div>
              <button onClick={() => setSelected(null)} aria-label="Stäng" className="text-muted-foreground hover:text-foreground"><X /></button>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <Info l="E-post" v={sel.email} />
                <Info l="Telefonnummer" v={sel.phone ?? "—"} />
                <Info l="Personnummer" v={sel.ssn ?? "—"} />
                <Info l="Födelsedatum" v={sel.birth_date ?? "—"} />
                <Info l="Kön" v={sel.gender ?? "—"} />
                <Info l="Adress" v={[sel.address, [sel.zip, sel.city].filter(Boolean).join(" ")].filter(Boolean).join(", ") || "—"} />
                <Info l="Operatör" v={sel.operator ?? "—"} />
                <Info l="Telefonmodell" v={`${sel.phone_brand ?? "—"}${sel.phone_model ? " · " + sel.phone_model : ""}`} />
                <Info l="Kundtyp" v={sel.customer_type ?? "—"} />
                <Info l="Hörde om Access via" v={sel.source ?? "—"} />
                <Info l="Medlem sedan" v={sel.member_since} />
                <Info l="Säljare" v={(sel as any).salesperson ?? "—"} />
                <Info l="GDPR-samtycke" v={sel.gdpr_consent ? "Ja" : "Nej"} />
                {(sel as any).binding_until && new Date((sel as any).binding_until) > new Date() && (
                  <Info l="Bindningstid t.o.m." v={new Date((sel as any).binding_until).toLocaleDateString("sv-SE", { day: "numeric", month: "long", year: "numeric" })} />
                )}
                {sel.cancel_at && (
                  <Info l="Uppsagd — avslutas" v={new Date(sel.cancel_at).toLocaleDateString("sv-SE", { day: "numeric", month: "long", year: "numeric" })} />
                )}
                {(sel as any).cancellation_reason && (
                  <Info
                    l="Uppsägningsorsak"
                    v={({
                      rabatt_reparation: "Tecknade främst för rabatt/reparation",
                      innehall_inte_intressant: "Utbildningar/medlemskvällar ej intressanta nog",
                      annan_losning: "Hittat annan lösning för teknikhjälp",
                      ovrigt: "Övrigt",
                    } as Record<string, string>)[(sel as any).cancellation_reason] ?? (sel as any).cancellation_reason}
                  />
                )}
                {(sel as any).cancellation_feedback && (
                  <Info l="Kommentar vid uppsägning" v={(sel as any).cancellation_feedback} />
                )}
              </div>

              {(sel.interests?.length || sel.interests_text) && (
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2 font-display">Vill lära sig mer om</div>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {(sel.interests ?? []).map((i) => (
                      <span key={i} className="text-xs rounded-full px-3 py-1 bg-primary/10 text-primary font-display">{i}</span>
                    ))}
                  </div>
                  {sel.interests_text && <p className="text-sm text-muted-foreground italic">"{sel.interests_text}"</p>}
                </div>
              )}

              {sel.notes && (
                <div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1 font-display">Anteckningar</div>
                  <p className="text-sm">{sel.notes}</p>
                </div>
              )}

              <MemberActivity memberId={sel.id} storeMap={storeMap} />

              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider mb-2 font-display">Status</div>
                {isAdmin ? (
                  <>
                    <select
                      value={sel.status}
                      onChange={(e) => setStatus.mutate({ id: sel.id, status: e.target.value })}
                      className="input-mini !text-sm max-w-[200px] capitalize"
                    >
                      {STATUSES.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
                    </select>
                    <p className="text-xs text-muted-foreground mt-2">Ändringen sparas direkt och syns för kunden.</p>
                  </>
                ) : (
                  <>
                    <span className={`inline-flex px-3 py-1.5 rounded-full text-sm font-display font-semibold capitalize ${sel.status === "aktiv" ? "bg-[var(--color-success)]/15 text-[var(--color-success)]" : "bg-[var(--color-destructive)]/15 text-[var(--color-destructive)]"}`}>{sel.status}</span>
                    <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1.5"><Lock size={11} /> Endast admin kan ändra status.</p>
                  </>
                )}
              </div>

              {isAdmin ? (
                <div className="pt-4 border-t border-border">
                  <div className="text-xs text-destructive uppercase tracking-wider mb-2 font-display font-bold">Farozon — permanent radering</div>
                  <p className="text-xs text-muted-foreground mb-3">Tar bort medlemmen, inloggningskontot och avslutar ev. prenumeration. Fakturahistorik bevaras för bokföring. Mejladressen kan registreras igen direkt. <strong className="text-foreground">Kan inte ångras.</strong></p>
                  <button
                    onClick={() => handleDelete(sel.id, `${sel.first_name} ${sel.last_name}`)}
                    disabled={remove.isPending}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-destructive text-white text-sm font-display font-semibold hover:bg-destructive/90 transition disabled:opacity-50"
                  >
                    <Trash2 size={14} /> {remove.isPending ? "Raderar…" : "Radera permanent"}
                  </button>
                </div>
              ) : (
                <div className="pt-4 border-t border-border">
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5"><Lock size={11} /> Radering av personuppgifter kräver admin-behörighet.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </StaffLayout>
  );
}
function Info({ l, v }: { l: string; v: string }) { return (<div><div className="text-xs text-muted-foreground uppercase tracking-wider">{l}</div><div className="font-display font-semibold break-words">{v}</div></div>); }

function MemberActivity({ memberId, storeMap }: { memberId: string; storeMap: Map<string, string> }) {
  const { data: logs = [], isLoading: logsLoading } = useMemberBenefitLogs(memberId);
  const { data: regs = [], isLoading: regsLoading } = useMemberEventRegistrations(memberId);

  return (
    <div className="space-y-6">
      <div className="pt-4 border-t border-border">
        <div className="text-xs text-muted-foreground uppercase tracking-wider mb-3 font-display flex items-center gap-2">
          <Tag size={12} /> Förmånsaktivitet ({logs.length})
        </div>
        {logsLoading && <p className="text-xs text-muted-foreground">Laddar…</p>}
        {!logsLoading && !logs.length && <p className="text-xs text-muted-foreground">Inga förmåner aktiverade ännu.</p>}
        {!!logs.length && (
          <ul className="space-y-2">
            {logs.slice(0, 20).map((l: any) => {
              const isRedeemed = !!l.redeemed_at;
              const isBooking = !!l.booking_at;
              const when = l.activated_at || l.used_at;
              return (
                <li key={l.id} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50 border border-border text-sm">
                  <div className={`mt-0.5 size-7 rounded-full flex items-center justify-center shrink-0 ${isRedeemed ? "bg-[var(--color-success)]/15 text-[var(--color-success)]" : "bg-primary/10 text-primary"}`}>
                    {isRedeemed ? <CheckCircle2 size={14} /> : <Clock size={14} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold">{l.benefit_label}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {isBooking
                        ? `Bokning ${new Date(l.booking_at).toLocaleString("sv-SE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}`
                        : `Aktiverad ${new Date(when).toLocaleString("sv-SE", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}`}
                      {l.store_id && storeMap.get(l.store_id) ? ` · ${storeMap.get(l.store_id)}` : ""}
                      {isRedeemed ? ` · Inlöst ${new Date(l.redeemed_at).toLocaleDateString("sv-SE")}` : ""}
                    </div>
                    {l.activation_code && !isRedeemed && (
                      <div className="text-[11px] text-muted-foreground mt-0.5">Kod: <span className="font-mono">{l.activation_code}</span></div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="pt-4 border-t border-border">
        <div className="text-xs text-muted-foreground uppercase tracking-wider mb-3 font-display flex items-center gap-2">
          <Calendar size={12} /> Eventanmälningar ({regs.length})
        </div>
        {regsLoading && <p className="text-xs text-muted-foreground">Laddar…</p>}
        {!regsLoading && !regs.length && <p className="text-xs text-muted-foreground">Inga eventanmälningar ännu.</p>}
        {!!regs.length && (
          <ul className="space-y-2">
            {regs.map((r: any) => (
              <li key={r.id} className="flex items-start gap-3 p-3 rounded-lg bg-muted/50 border border-border text-sm">
                <div className={`mt-0.5 size-7 rounded-full flex items-center justify-center shrink-0 ${r.checked_in_at ? "bg-[var(--color-success)]/15 text-[var(--color-success)]" : "bg-primary/10 text-primary"}`}>
                  {r.checked_in_at ? <CheckCircle2 size={14} /> : <Calendar size={14} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold">{r.event?.title ?? "—"}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {r.event?.starts_at ? new Date(r.event.starts_at).toLocaleString("sv-SE", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                    {r.event?.store_id && storeMap.get(r.event.store_id) ? ` · ${storeMap.get(r.event.store_id)}` : ""}
                    {r.checked_in_at ? " · Incheckad" : ""}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <EventSignup memberId={memberId} storeMap={storeMap} regs={regs} />
    </div>
  );
}

function EventSignup({ memberId, storeMap, regs }: { memberId: string; storeMap: Map<string, string>; regs: any[] }) {
  const { data: events = [], isLoading } = useEvents({ onlyActive: true, upcomingOnly: true });
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [busy, setBusy] = useState<string | null>(null);

  const eventIds = useMemo(() => events.map((e) => e.id), [events]);
  const { data: counts } = useQuery({
    queryKey: ["event_attendee_counts", eventIds],
    enabled: eventIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_event_attendee_counts" as any, { _event_ids: eventIds });
      if (error) throw error;
      const map = new Map<string, number>();
      ((data ?? []) as { event_id: string; count: number }[]).forEach((r) => map.set(r.event_id, Number(r.count) || 0));
      return map;
    },
  });

  const regByEvent = useMemo(() => {
    const m = new Map<string, any>();
    regs.forEach((r) => { if (r.event?.id) m.set(r.event.id, r); });
    return m;
  }, [regs]);

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["member_event_registrations", memberId] });
    qc.invalidateQueries({ queryKey: ["event_attendee_counts"] });
    qc.invalidateQueries({ queryKey: ["event_registrations"] });
  }

  async function toggle(ev: any) {
    const existing = regByEvent.get(ev.id);
    setBusy(ev.id);
    try {
      if (existing) {
        if (existing.checked_in_at) {
          toast.error("Medlemmen är redan incheckad – anmälan kan inte tas bort.");
          return;
        }
        const ok = await confirm({
          title: "Ta bort anmälan?",
          description: `${ev.title} – medlemmens anmälan tas bort.`,
          confirmText: "Ta bort",
          destructive: true,
        });
        if (!ok) return;
        const { error } = await supabase
          .from("event_registrations")
          .delete()
          .eq("event_id", ev.id)
          .eq("member_id", memberId)
          .is("checked_in_at", null);
        if (error) throw error;
        toast.success("Anmälan borttagen");
      } else {
        const used = counts?.get(ev.id) ?? 0;
        if (ev.max_attendees > 0 && used >= ev.max_attendees) {
          const ok = await confirm({
            title: "Eventet är fullt",
            description: `Eventet är fullt (${used}/${ev.max_attendees}) – anmäl ändå?`,
            confirmText: "Anmäl ändå",
          });
          if (!ok) return;
        }
        const { error } = await supabase
          .from("event_registrations")
          .upsert({ event_id: ev.id, member_id: memberId, status: "confirmed" } as any, { onConflict: "event_id,member_id" });
        if (error) throw error;
        toast.success("Medlemmen är anmäld");
      }
      invalidate();
    } catch (e: any) {
      toast.error(e?.message ?? "Något gick fel");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="pt-4 border-t border-border">
      <div className="text-xs text-muted-foreground uppercase tracking-wider mb-3 font-display flex items-center gap-2">
        <Calendar size={12} /> Anmäl till event
      </div>
      {isLoading && <p className="text-xs text-muted-foreground">Laddar…</p>}
      {!isLoading && !events.length && <p className="text-xs text-muted-foreground">Inga kommande event.</p>}
      <ul className="space-y-2">
        {events.map((ev) => {
          const reg = regByEvent.get(ev.id);
          const used = counts?.get(ev.id) ?? 0;
          const full = ev.max_attendees > 0 && used >= ev.max_attendees;
          const locked = !!reg?.checked_in_at;
          return (
            <li key={ev.id} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border border-border text-sm">
              <button
                type="button"
                onClick={() => toggle(ev)}
                disabled={busy === ev.id || locked}
                aria-label={reg ? "Avanmäl" : "Anmäl"}
                className="shrink-0 text-primary disabled:opacity-50"
              >
                {reg ? <CheckSquare size={18} /> : <Square size={18} className="text-muted-foreground" />}
              </button>
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold truncate">{ev.title}</div>
                <div className="text-xs text-muted-foreground mt-0.5">
                  {new Date(ev.starts_at).toLocaleString("sv-SE", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  {ev.store_id && storeMap.get(ev.store_id) ? ` · ${storeMap.get(ev.store_id)}` : ""}
                  {locked ? " · Incheckad" : ""}
                </div>
              </div>
              <span className={`text-xs font-display font-semibold shrink-0 ${full ? "text-destructive" : "text-muted-foreground"}`}>
                {used}/{ev.max_attendees || "∞"}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default CustomersPage;
